/**
 * LeadTimeContext - Provides active lead context for time tracking
 * Pages set the active lead when the user is working on a lead/proposal
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

interface LeadTimeContextValue {
  activeLeadId: string | null;
  activeDealId: string | null;
  activeProposalId: string | null;
  setActiveLead: (leadId: string | null, dealId?: string | null, proposalId?: string | null) => void;
}

const LeadTimeContext = createContext<LeadTimeContextValue | undefined>(undefined);

export const useLeadTimeContext = () => {
  const ctx = useContext(LeadTimeContext);
  if (ctx === undefined) {
    throw new Error('useLeadTimeContext must be used within LeadTimeProvider');
  }
  return ctx;
};

export const LeadTimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeLeadId, setActiveLeadId] = useState<string | null>(null);
  const [activeDealId, setActiveDealId] = useState<string | null>(null);
  const [activeProposalId, setActiveProposalId] = useState<string | null>(null);

  const setActiveLead = useCallback(
    (leadId: string | null, dealId?: string | null, proposalId?: string | null) => {
      setActiveLeadId(leadId);
      setActiveDealId(dealId ?? null);
      setActiveProposalId(proposalId ?? null);
    },
    []
  );

  const value: LeadTimeContextValue = {
    activeLeadId,
    activeDealId,
    activeProposalId,
    setActiveLead,
  };

  return (
    <LeadTimeContext.Provider value={value}>
      {children}
    </LeadTimeContext.Provider>
  );
};
